import {
  useState,
  useEffect,
  useCallback,
  useSyncExternalStore,
} from "react";

// Per-viewer UI preference backed by localStorage. Storage can be blocked
// (private mode, disabled site data), so every access is guarded and the
// fallback value is used when it fails.
function readStored(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

function writeStored(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Preference just won't persist
  }
}

const THEME_COLORS = { light: "#eef0ec", dark: "#111412" };

// "light" | "dark", shared by every caller (sidebar and the mobile header
// both toggle it). index.html applies the saved value before first paint;
// applyTheme keeps <html data-theme> and the browser chrome color in sync.
let currentTheme = readStored("theme", "light") === "dark" ? "dark" : "light";
const themeListeners = new Set();

function applyTheme(theme) {
  currentTheme = theme;
  document.documentElement.setAttribute("data-theme", theme);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLORS[theme]);
  writeStored("theme", theme);
  themeListeners.forEach((l) => l());
}

const subscribeTheme = (listener) => {
  themeListeners.add(listener);
  return () => themeListeners.delete(listener);
};

export function useTheme() {
  const theme = useSyncExternalStore(subscribeTheme, () => currentTheme);
  const toggleTheme = useCallback(
    () => applyTheme(currentTheme === "dark" ? "light" : "dark"),
    [],
  );
  return [theme, toggleTheme];
}

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(
    () => readStored("sidebar_collapsed", "0") === "1",
  );

  useEffect(() => {
    writeStored("sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  return [collapsed, toggle];
}
